import { useMemo } from 'react'
import { useReadContracts, useReadContract, useAccount } from 'wagmi'
import { Address, formatUnits } from 'viem'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { EVaultData } from '@/features/vaults/logic/useEVaults'
import { getChainConfigEntry } from '@/config/chain'
import { useEVaultWeightedAPY } from './useEVaultWeightedAPY'
import { getEntities } from '../data/entities'
import { selectGovernorsFromAddresses } from './selectGovernors'
import { getVaultMarket } from '../data/products'
import { useTokenInfo, type NormalizedTokenInfo, useEVaultInfo } from '@/domain/euler'
import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { useEVaultStrategies } from './useEVaultStrategies'

export interface UseEVaultDetailsParams {
  vaultAddress: CheckedAddress
}

export interface EVaultDetailsData extends EVaultData {
  supplyAPY: number
  totalAssets: NormalizedUnitNumber
  availableAssets?: NormalizedUnitNumber
  lastTotalAssets: NormalizedUnitNumber
  underlyingAsset: Address
  assetSymbol?: string
  underlyingTokenInfo?: NormalizedTokenInfo // Full token info from Euler indexer
  curatorName?: string
  sharePrice: NormalizedUnitNumber // 1 share = X underlying assets
  exchangeRate: number // calculated exchange rate for display
  owner: Address
  guardian: Address
  curator: Address
  timelock: bigint // Timelock in seconds
  performanceFee: bigint // Performance fee in basis points (1e18 = 100%)
  allocator?: Address // Optional since there may be multiple allocators
  oracle?: Address // Oracle address for vault-specific price fetching
  unitOfAccount?: Address // Unit of account for vault-specific price fetching
  supplyCap?: NormalizedUnitNumber // Supply cap (eEarn vaults don't support on-chain cap queries)
  lendingVaultAddresses?: Address[] // Lending strategy addresses — VaultLens on them gives the asset price
}

export interface UseEVaultDetailsResult {
  evault: EVaultDetailsData | null
  chainId: number
  chainName: string
  chainMismatch: boolean
  isLoading: boolean
}

export function useEVaultDetails({ vaultAddress }: UseEVaultDetailsParams): UseEVaultDetailsResult {
  const { address: userAddress } = useAccount()

  // Always use Base mainnet for eVaults (ignoring chainId param)
  const actualChainId = E_EARN_VAULT_CHAIN_ID
  const chainConfig = getChainConfigEntry(actualChainId)
  const chainName = chainConfig.meta?.name || 'Unknown Chain'
  const chainMismatch = false // No chain mismatch for eVaults since they're always on Base

  // Get weighted APY from strategy allocations (loads in background, doesn't block page)
  const { weightedAPY } = useEVaultWeightedAPY({ vaultAddress })

  // Fetch core vault data - this query is STABLE and doesn't depend on userAddress
  // This prevents flickering when wallet connects/disconnects
  const { data: vaultData, isLoading: isLoadingVaultData } = useReadContracts({
    contracts: [
      // Index 0: name
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'name',
        chainId: actualChainId,
      },
      // Index 1: symbol
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'symbol',
        chainId: actualChainId,
      },
      // Index 2: decimals
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'decimals',
        chainId: actualChainId,
      },
      // Index 3: totalSupply
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'totalSupply',
        chainId: actualChainId,
      },
      // Index 4: totalAssets
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'totalAssets',
        chainId: actualChainId,
      },
      // Index 5: lastTotalAssets
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'lastTotalAssets',
        chainId: actualChainId,
      },
      // Index 6: asset (underlying)
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'asset',
        chainId: actualChainId,
      },
      // Index 7: owner
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'owner',
        chainId: actualChainId,
      },
      // Index 8: guardian
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'guardian',
        chainId: actualChainId,
      },
      // Index 9: curator
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'curator',
        chainId: actualChainId,
      },
      // Index 10: timelock
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'timelock',
        chainId: actualChainId,
      },
      // Index 11: fee
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'fee',
        chainId: actualChainId,
      },
    ],
    query: {
      staleTime: 30_000, // Cache for 30 seconds to prevent unnecessary refetches
      refetchInterval: 30_000, // Refetch every 30 seconds for fresh data
    },
  })

  // Fetch user balance SEPARATELY to avoid causing main query to refetch when wallet connects
  // This loads in the background and doesn't block page rendering
  const { data: userBalanceRaw } = useReadContract({
    address: vaultAddress as Address,
    abi: eEarnVaultImplementationAbi,
    functionName: 'balanceOf',
    args: userAddress ? [userAddress] : undefined,
    chainId: actualChainId,
    query: {
      enabled: !!userAddress,
      staleTime: 30_000,
      refetchInterval: 30_000,
    },
  })

  // Extract decimals early for convertToAssets query
  const vaultDecimals = (vaultData?.[2]?.result as number) ?? 18

  // Fetch convertToAssets using actual decimals (1 whole share = 10^decimals raw units)
  const { data: sharePriceRaw } = useReadContract({
    address: vaultAddress as Address,
    abi: eEarnVaultImplementationAbi,
    functionName: 'convertToAssets',
    args: [BigInt(10 ** vaultDecimals)],
    chainId: actualChainId,
    query: {
      enabled: !!vaultData, // Only run after main data is fetched
      staleTime: 30_000,
      refetchInterval: 30_000,
    },
  })

  // Extract underlyingAsset early to fetch its symbol
  const underlyingAssetAddress = vaultData?.[6]?.result as Address | undefined
  const curatorAddress = vaultData?.[9]?.result as Address | undefined

  // Fetch underlying token info from Euler indexer (with onchain fallback)
  // Note: We don't include this loading state in the main isLoading to prevent waterfall flicker
  const { token: underlyingTokenInfo } = useTokenInfo(actualChainId, underlyingAssetAddress)
  // Use useEVaultInfo which returns vault data from indexer or on-chain fallback
  const { vault: vaultInfo } = useEVaultInfo(vaultAddress as Address, actualChainId)

  // On-chain strategies (always available, doesn't depend on indexer)
  const { allStrategies: onchainStrategies } = useEVaultStrategies({ vaultAddress })

  // All lending strategies — VaultLens on them gives the shared underlying asset price.
  // Indexer strategies preferred; on-chain as fallback when indexer is unavailable.
  const lendingVaultAddresses: Address[] = useMemo(() => {
    const indexerAddrs = (vaultInfo?.strategies ?? []).map((s) => s.strategy as Address).filter(Boolean)
    if (indexerAddrs.length > 0) return indexerAddrs
    return onchainStrategies.map((s) => s.address).filter(Boolean)
  }, [vaultInfo?.strategies, onchainStrategies])

  // Resolve curator name from governor address
  const entities = getEntities()
  const governorData = curatorAddress ? selectGovernorsFromAddresses(curatorAddress, entities) : { governors: [] }
  const governorCuratorName = governorData.governors[0]?.name || ''

  // Prefer product/market entity label for Earn vaults (e.g. Clearstar)
  const market = getVaultMarket(vaultAddress as any)
  const entityKeys = market ? (Array.isArray(market.entity) ? market.entity : [market.entity]) : []
  const marketCuratorName = entityKeys.map((k) => entities.get(k)?.name).find(Boolean) || ''

  const curatorName = marketCuratorName || governorCuratorName

  // Check if we have valid vault data - allow for some fields to be optional
  // At minimum, we need name, symbol, decimals, and totalSupply to exist
  const hasValidVaultData =
    vaultData &&
    vaultData[0]?.status === 'success' &&
    vaultData[0]?.result && // name
    vaultData[1]?.status === 'success' &&
    vaultData[1]?.result && // symbol
    vaultData[2]?.status === 'success' &&
    vaultData[2]?.result !== undefined && // decimals (can be 0)
    vaultData[3]?.status === 'success' &&
    vaultData[3]?.result !== undefined // totalSupply (can be 0)

  // Return early if no data yet (initial load)
  // This pattern matches eLend - return isLoading only on initial load, not refetches
  if (!vaultData) {
    return {
      evault: null,
      chainId: actualChainId,
      chainName,
      chainMismatch,
      isLoading: isLoadingVaultData,
    }
  }

  let evault: EVaultDetailsData | null = null

  if (hasValidVaultData && vaultData) {
    const name = (vaultData[0]?.result as string) || ''
    const symbol = (vaultData[1]?.result as string) || ''
    const decimals = (vaultData[2]?.result as number) ?? 18
    const totalSupply = (vaultData[3]?.result as bigint) ?? 0n
    const totalAssets = vaultData[4]?.result as bigint | undefined
    // Only use availableAssets from indexer - on-chain calculation is not reliable
    // Show "-" when indexer is unavailable
    const availableAssets = vaultInfo?.source === 'indexer' ? vaultInfo.availableAssets : undefined
    const lastTotalAssets = vaultData[5]?.result as bigint | undefined
    const underlyingAsset = underlyingAssetAddress
    const owner = vaultData[7]?.result as Address | undefined
    const guardian = vaultData[8]?.result as Address | undefined
    const curator = curatorAddress
    const timelock = vaultData[10]?.result as bigint | undefined
    const performanceFee = vaultData[11]?.result as bigint | undefined
    // User balance comes from separate query to avoid refetch flicker
    const userBalance = userBalanceRaw as bigint | undefined

    // Note: eEarn vaults (ERC4626 aggregators) don't have the caps() function like EVK vaults do
    // They may enforce caps at the aggregator level, but there's no on-chain way to query them
    const supplyCap = undefined

    // Calculate share price: use convertToAssets for accurate calculation
    // convertToAssets(10^decimals) returns assets for 1 whole share (in underlying raw units)
    let sharePrice: NormalizedUnitNumber
    let exchangeRate = 1 // Default 1:1

    if (sharePriceRaw !== undefined && sharePriceRaw > 0n) {
      // Use convertToAssets result (assets per 1 share)
      sharePrice = NormalizedUnitNumber(formatUnits(sharePriceRaw, decimals))
      exchangeRate = Number(sharePrice)
    } else if (totalAssets !== undefined && totalSupply > 0n) {
      // Fallback: calculate from totalAssets / totalSupply
      exchangeRate = Number(totalAssets) / Number(totalSupply)
      sharePrice = NormalizedUnitNumber(exchangeRate.toString())
    } else {
      sharePrice = NormalizedUnitNumber('1')
    }

    // Use weighted APY from strategy allocations instead of growth-based calculation
    const supplyAPY = weightedAPY

    evault = {
      address: vaultAddress as Address,
      name,
      symbol,
      decimals,
      totalSupply: NormalizedUnitNumber(formatUnits(totalSupply, decimals)),
      totalAssets:
        totalAssets !== undefined
          ? NormalizedUnitNumber(formatUnits(totalAssets, decimals))
          : NormalizedUnitNumber('0'),
      availableAssets:
        availableAssets !== undefined ? NormalizedUnitNumber(formatUnits(availableAssets, decimals)) : undefined,
      lastTotalAssets:
        lastTotalAssets !== undefined
          ? NormalizedUnitNumber(formatUnits(lastTotalAssets, decimals))
          : NormalizedUnitNumber('0'),
      tvlUsd: undefined,
      underlyingAsset: underlyingAsset || ('0x0000000000000000000000000000000000000000' as Address),
      assetSymbol: underlyingTokenInfo?.symbol,
      underlyingTokenInfo,
      curatorName,
      sharePrice,
      exchangeRate,
      supplyAPY,
      userBalance: userBalance !== undefined ? NormalizedUnitNumber(formatUnits(userBalance, decimals)) : undefined,
      owner: owner || ('0x0000000000000000000000000000000000000000' as Address),
      guardian: guardian || ('0x0000000000000000000000000000000000000000' as Address),
      curator: curator || ('0x0000000000000000000000000000000000000000' as Address),
      timelock: timelock || 0n,
      performanceFee: performanceFee || 0n,
      supplyCap,
      lendingVaultAddresses: lendingVaultAddresses.length > 0 ? lendingVaultAddresses : undefined,
    }
  }

  return {
    evault,
    chainId: actualChainId,
    chainName,
    chainMismatch,
    // Once we have vaultData from the RPC, return isLoading: false
    // This matches eLend pattern - skeleton only shows on initial load, not refetches
    isLoading: false,
  }
}
